"use client";

import { useTranslations } from "next-intl";
import { logoUrl } from "@/features/media/mediaUrl";
import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useVisit360Modal } from "../useVisit360Modal";

/**
 * A 360° tour, full-screen.
 *
 * Not the gallery carousel: the tour is its own navigable space and wants the
 * whole viewport, so the only chrome is a way back — the tenant's logo on the
 * left, doubling as the exit, plus a close button for anyone looking for one.
 */
export function Visit360Modal() {
  const t = useTranslations();
  const configs = useProjectConfigs();
  const media = useMediaContext();
  const { isOpen, url, close } = useVisit360Modal();

  if (!isOpen || !url) return null;

  const logo = logoUrl(configs.appLogos?.[0]?.image?.fileName, media.domain);
  const backLabel = t("modal.streetView.buttons.backToMap");

  return (
    <div className="fixed inset-0 z-50 bg-black dark:bg-dark-color">
      <section className="flex h-full items-center justify-center">
        <div id="street-view-container" className="relative h-full w-full">
          <button
            type="button"
            className="absolute left-0 top-0 z-10 flex items-center justify-center rounded-r-full bg-white px-5 py-2.5 dark:bg-dark-color"
            onClick={close}
          >
            {logo && (
              // Tenant-uploaded, arbitrary dimensions, same-origin.
              // eslint-disable-next-line @next/next/no-img-element
              <img className="h-[36px]" src={logo} alt="" />
            )}
            <span className="sidebar-toggle ml-5 text-sm font-bold uppercase dark:text-off-white">
              {backLabel}
            </span>
          </button>

          <button
            type="button"
            className="absolute right-2 top-2 inline-flex items-center rounded-full bg-white p-2 text-black hover:bg-[var(--primary)] hover:text-[var(--dynamic-text-color)] dark:text-white"
            aria-label={backLabel}
            onClick={close}
          >
            <i className="icon ic-material_ic_close_18px text-2xl" />
          </button>

          <iframe
            title={backLabel}
            src={url}
            allowFullScreen
            className="h-full w-full"
          />
        </div>
      </section>
    </div>
  );
}
